import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { RouterModule } from '@angular/router';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { ButtonsOptions, HorizontalAlignmentOptions, TextStyleOptions } from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { ShellComponent } from '@df/ui/common/shell/shell.component';
import { GSAPModule } from '@df/ui/display/gsap/gsap.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { LocalVimeoPlayerComponent } from '../../../../../ui/display/media/vimeo-player.component';
import { BASIC_COMPONENT_FIELDS, CMS_COMPONENT_CWV_CONFIG, ICmsComponentCwvConfig } from '../../../../config/component.config';

export interface PageComponentFullWidthBannerV2ComponentData extends NICmsContentComponent.IData, ICmsComponentCwvConfig {
    text_placement: 'on_image_text_box' | 'centered_text_box';
    show_image_mobile: 'show_1_img' | 'show_2_img';
    image_1: string;
    video_id_1: string;
    link_1: string;
    align_1: string;
    title_1: string;
    heading_1: string;
    title_style_1: string;
    text_color_1: string;
    cta_text_1: string;
    cta_style_1: string;
    caption_1: string;
    image_2: string;
    video_id_2: string;
    link_2: string;
    align_2: string;
    title_2: string;
    heading_2: string;
    title_style_2: string;
    text_color_2: string;
    cta_text_2: string;
    cta_style_2: string;
    caption_2: string;
    link_3: string;
    align_box: string;
    align_3: string;
    title_3: string;
    heading_3: string;
    title_style_3: string;
    text_color_3: string;
    cta_text_3: string;
    cta_style_3: string;
    caption_3: string;
    second_cta_text?: string;
    second_cta_link?: string;
    second_cta_style?: string;
    align_cta?: string;
}

@ICmsComponent<PageComponentFullWidthBannerV2Component>({
    id: 'full-width-banner-v2',
    name: 'Full Width Banner (Variat 2)',
    image: 'assets/images/cms/full-width-banner-v2.png',
    tags: ['Homepage', 'Banner', 'Video'],
    fields: [
        {
            type: NICmsContentField.EFieldType.label,
            key: 'general',
            title: 'General'
        },
        <NICmsContentField.IChoiceField>{
            key: 'text_placement',
            type: NICmsContentField.EFieldType.choice,
            title: 'Text placement',
            required: true,
            default: 'centered_text_box',
            options: [
                { key: 'on_image_text_box', title: 'Text box on images (one or both)' },
                { key: 'centered_text_box', title: 'Single centered text box' }
            ]
        },
        <NICmsContentField.IChoiceField>{
            key: 'show_image_mobile',
            type: NICmsContentField.EFieldType.choice,
            title: 'Show chosen image for mobile devices',
            required: true,
            default: 'show_1_img',
            options: [
                { key: 'show_1_img', title: '1st (left) image/video' },
                { key: 'show_2_img', title: '2nd (right) image/video' }
            ]
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'image-section',
            title: '1st (left) image/video'
        },
        {
            type: NICmsContentField.EFieldType.image,
            key: 'image_1',
            title: 'Image 1',
            description: 'If both image and video are set then image will be use as a placeholder till video is loaded',
            default: ''
        },
        {
            key: 'video_id_1',
            type: NICmsContentField.EFieldType.text,
            title: 'ID or URL',
            description: 'Video ID or URL, e.g. https://vimeo.com/442627127'
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-content',
            title: '1st (left) image/video content',
            canShow: data => data.text_placement === 'on_image_text_box'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'link_1',
            title: 'Link',
            default: '/',
            canShow: data => data.text_placement === 'on_image_text_box'
        },
        <NICmsContentField.IChoiceField>{
            key: 'align_1',
            type: NICmsContentField.EFieldType.choice,
            title: 'Text Alignment',
            options: HorizontalAlignmentOptions,
            default: 'center',
            canShow: data => data.text_placement === 'on_image_text_box'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'heading_1',
            title: 'Heading',
            default: 'Amet',
            canShow: data => data.text_placement === 'on_image_text_box'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'title_1',
            title: 'Title',
            default: 'Lorem ipsum',
            canShow: data => data.text_placement === 'on_image_text_box'
        },
        <NICmsContentField.IField>{
            key: 'text_color_1',
            type: NICmsContentField.EFieldType.colorPicker,
            title: 'Text Color',
            canShow: data => data.text_placement === 'on_image_text_box'
        },
        <NICmsContentField.IChoiceField>{
            key: 'title_style_1',
            type: NICmsContentField.EFieldType.choice,
            title: 'Title Style',
            options: TextStyleOptions,
            canShow: data => data.text_placement === 'on_image_text_box'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'cta_text_1',
            title: 'Button Label',
            default: 'Find out more',
            canShow: data => data.text_placement === 'on_image_text_box'
        },
        <NICmsContentField.IChoiceField>{
            key: 'cta_style_1',
            type: NICmsContentField.EFieldType.choice,
            title: 'Button Style',
            options: ButtonsOptions,
            canShow: data => data.text_placement === 'on_image_text_box'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'caption_1',
            title: 'Caption',
            default: 'dolor sit amet consectetur',
            canShow: data => data.text_placement === 'on_image_text_box'
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'image-section',
            title: '2nd (right) image/video'
        },
        {
            type: NICmsContentField.EFieldType.image,
            key: 'image_2',
            title: 'Image 2',
            description: 'If both image and video are set then image will be use as a placeholder till video is loaded',
            default: ''
        },
        {
            key: 'video_id_2',
            type: NICmsContentField.EFieldType.text,
            title: 'ID or URL',
            description: 'Video ID or URL, e.g. https://vimeo.com/442627127'
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-content',
            title: '2nd (right) image/video content',
            canShow: data => data.text_placement === 'on_image_text_box'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'link_2',
            title: 'Link',
            default: '/',
            canShow: data => data.text_placement === 'on_image_text_box'
        },
        <NICmsContentField.IChoiceField>{
            key: 'align_2',
            type: NICmsContentField.EFieldType.choice,
            title: 'Text Alignment',
            options: HorizontalAlignmentOptions,
            default: 'center',
            canShow: data => data.text_placement === 'on_image_text_box'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'heading_2',
            title: 'Heading',
            default: 'Amet',
            canShow: data => data.text_placement === 'on_image_text_box'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'title_2',
            title: 'Title',
            default: 'Lorem ipsum',
            canShow: data => data.text_placement === 'on_image_text_box'
        },
        <NICmsContentField.IField>{
            key: 'text_color_2',
            type: NICmsContentField.EFieldType.colorPicker,
            title: 'Text Color',
            canShow: data => data.text_placement === 'on_image_text_box'
        },
        <NICmsContentField.IChoiceField>{
            key: 'title_style_2',
            type: NICmsContentField.EFieldType.choice,
            title: 'Title Style',
            options: TextStyleOptions,
            canShow: data => data.text_placement === 'on_image_text_box'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'cta_text_2',
            title: 'Button Label',
            default: 'Find out more',
            canShow: data => data.text_placement === 'on_image_text_box'
        },
        <NICmsContentField.IChoiceField>{
            key: 'cta_style_2',
            type: NICmsContentField.EFieldType.choice,
            title: 'Button Style',
            options: ButtonsOptions,
            canShow: data => data.text_placement === 'on_image_text_box'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'caption_2',
            title: 'Caption',
            default: 'dolor sit amet consectetur',
            canShow: data => data.text_placement === 'on_image_text_box'
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-content',
            title: 'Component content',
            canShow: data => data.text_placement === 'centered_text_box'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'link_3',
            title: 'Link',
            default: '/',
            canShow: data => data.text_placement === 'centered_text_box'
        },
        <NICmsContentField.IChoiceField>{
            key: 'align_box',
            type: NICmsContentField.EFieldType.choice,
            title: 'Text box placement',
            required: true,
            default: 'flex-justify-center',
            options: [
                { key: 'flex-justify-start', title: 'Left' },
                { key: 'flex-justify-center', title: 'Center' },
                { key: 'flex-justify-end', title: 'Right' }
            ],
            canShow: data => data.text_placement === 'centered_text_box'
        },
        <NICmsContentField.IChoiceField>{
            key: 'align_3',
            type: NICmsContentField.EFieldType.choice,
            title: 'Text Alignment',
            options: HorizontalAlignmentOptions,
            default: 'center',
            canShow: data => data.text_placement === 'centered_text_box'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'heading_3',
            title: 'Heading',
            default: 'Amet',
            canShow: data => data.text_placement === 'centered_text_box'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'title_3',
            title: 'Title',
            default: 'Lorem ipsum',
            canShow: data => data.text_placement === 'centered_text_box'
        },
        <NICmsContentField.IField>{
            key: 'text_color_3',
            type: NICmsContentField.EFieldType.colorPicker,
            title: 'Text Color',
            canShow: data => data.text_placement === 'centered_text_box'
        },
        <NICmsContentField.IChoiceField>{
            key: 'title_style_3',
            type: NICmsContentField.EFieldType.choice,
            title: 'Title Style',
            options: TextStyleOptions,
            canShow: data => data.text_placement === 'centered_text_box'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'cta_text_3',
            title: 'Button Label',
            default: 'Find out more',
            canShow: data => data.text_placement === 'centered_text_box'
        },
        <NICmsContentField.IChoiceField>{
            key: 'cta_style_3',
            type: NICmsContentField.EFieldType.choice,
            title: 'Button Style',
            options: ButtonsOptions,
            canShow: data => data.text_placement === 'centered_text_box'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'second_cta_text',
            title: 'Second Button Label',
            default: 'Find out more',
            canShow: data => data.text_placement === 'centered_text_box'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'second_cta_link',
            title: 'Second Button Link',
            default: '/',
            canShow: data => data.text_placement === 'centered_text_box'
        },
        <NICmsContentField.IChoiceField>{
            key: 'second_cta_style',
            type: NICmsContentField.EFieldType.choice,
            title: 'Second Button Style',
            options: ButtonsOptions,
            canShow: data => data.text_placement === 'centered_text_box'
        },
        <NICmsContentField.IChoiceField>{
            key: 'align_cta',
            type: NICmsContentField.EFieldType.choice,
            title: 'Button align',
            required: true,
            default: 'horizontally',
            options: [
                { key: 'horizontally', title: 'Horizontally' },
                { key: 'vertically', title: 'Vertically' }
            ]
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'caption_3',
            title: 'Caption',
            default: 'dolor sit amet consectetur',
            canShow: data => data.text_placement === 'centered_text_box'
        },
        ...BASIC_COMPONENT_FIELDS,
        ...CMS_COMPONENT_CWV_CONFIG
    ]
})
@Component({
    selector: 'page-component-full-width-banner-v2',
    templateUrl: './full-width-banner-v2.html',
    styleUrl: './../full-width-banner/full-width-banner.scss',
    standalone: true,
    imports: [
        CommonModule,
        DfImageDirective,
        HrefComponent,
        ICmsClientModule,
        GSAPModule,
        LocalVimeoPlayerComponent,
        RouterModule,
        ShellComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageComponentFullWidthBannerV2Component extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: PageComponentFullWidthBannerV2ComponentData;
}
